








































































import { NetworkUploadType, validNetworkUploadType } from 'multinet';
import {
  computed, defineComponent, Ref, ref,
} from 'vue';

import api from '@/api';
import { NetworkFileType } from '@/types';

const fileTypes: NetworkFileType[] = [
  {
    displayName: 'D3 JSON (ext: .json)',
    hint: 'JSON format compatible with d3-force',
    queryCall: 'd3_json',
    extension: ['json'],
  },
  {
    displayName: 'Arbor Nested Tree (ext: .json)',
    hint: 'JSON-encoded tree format used by the Arbor project',
    queryCall: 'nested_json',
    extension: ['json'],
  },
  {
    displayName: 'Newick Tree (ext: .phy, .tree)',
    hint: 'The Newick Standard for representing trees in computer-readable form',
    queryCall: 'newick',
    extension: ['phy', 'tree'],
  },
];

export default defineComponent({
  name: 'NetworkUploadForm',

  props: {
    workspace: {
      type: String,
      required: true,
    },
  },

  setup(props, { emit }) {
    const tableCreationError: Ref<string | null> = ref(null);
    const fileUploadError : Ref<string | null> = ref(null);
    const fileName: Ref<string | null> = ref(null);
    const selectedType: Ref<NetworkFileType | null> = ref(null);
    const file: Ref<File | null> = ref(null);
    const uploading = ref(false);
    const uploadProgress: Ref<number | null> = ref(null);

    const createDisabled = computed(() => (
      !file.value
      || !fileName.value
      || !selectedType.value
      || !!fileUploadError.value
    ));

    function handleUploadProgress(event: { loaded: number; total: number; [key: string]: unknown }) {
      uploadProgress.value = (event.loaded / event.total) * 100;
    }

    function fileInfo(fileInput: File): [string, NetworkFileType] | null {
      if (!fileInput) {
        return null;
      }

      const [fileNameSplit, ...extensions] = fileInput.name.split('.');
      const extension = extensions[extensions.length - 1];

      const found = fileTypes.find((type) => type.extension.includes(extension) && validNetworkUploadType(type.queryCall));

      return found === undefined ? null : [fileNameSplit, found];
    }

    function handleFileInput(fileInput: File) {
      file.value = fileInput;

      if (!file.value) {
        selectedType.value = null;
        fileUploadError.value = null;
        return;
      }

      const fileInfoResult = fileInfo(file.value);
      if (fileInfoResult !== null) {
        const [fileNameResult, selectedTypeResult] = fileInfoResult;
        fileName.value = fileName.value || fileNameResult;
        selectedType.value = selectedTypeResult;
        fileUploadError.value = null;
      } else {
        fileUploadError.value = 'Invalid file type';
        selectedType.value = null;
      }
    }

    async function createNetwork() {
      if (file.value === null || fileName.value === null) {
        throw new Error('Valid file must be selected.');
      }

      if (selectedType.value === null) {
        throw new Error('`selectedType` is null, which is impossible');
      }

      uploading.value = true;

      try {
        await api.uploadNetwork(props.workspace, fileName.value, {
          type: selectedType.value.queryCall as NetworkUploadType,
          data: file.value,
        });

        tableCreationError.value = null;
        emit('success');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        if (err.status === 409) {
          tableCreationError.value = `Table "${fileName}" already exists`;
        } else if (err.status === 415) {
          tableCreationError.value = 'Data could not be read as CSV';
        } else {
          tableCreationError.value = 'Unknown error; please see developer console for details';
          throw err;
        }
      } finally {
        uploading.value = false;
        uploadProgress.value = null;
      }
    }

    return {
      fileTypes,
      tableCreationError,
      fileUploadError,
      fileName,
      selectedType,
      file,
      uploading,
      uploadProgress,
      createDisabled,
      handleUploadProgress,
      handleFileInput,
      createNetwork,
    };
  },
});
